/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useIntl } from 'react-intl'
import { useCallback, useContext } from 'react'
import { AuthContext } from '../../../../auth/components/AuthProvider'
import LeaseTemplateSchema from '../../../schemas/LeaseTemplateSchema.mjs'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../../client/js/lib/form'
import {
  patchLeaseTemplate,
  postLeaseTemplate,
  putLeaseTemplateFile
} from '../../../lib/rest/leaseTemplateAPIClient'
import SmartForm from '../../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../../client/js/components/RecordMetas'
import FormSection from '../../FormSection'
import NameField from '../../fields/NameField'
import FurnishedField from '../../fields/FurnishedField'
import ManagedField from '../../fields/ManagedField'
import DurationField from '../../fields/DurationField'
import FileDropZone from '../../file/FileDropZone'
import ButtonGroup from '../../../../../client/js/components/form/ButtonGroup'
import FormSaveButton from '../../../../../client/js/components/form/buttons/FormSaveButton'
import { useMutation } from '@tanstack/react-query'
import { getLeaseTemplateListLink } from '../../../lib/links/leaseTemplateLinks'
import { Box, Group, SimpleGrid, Stack } from '@mantine/core'
import FormResetButton from '../../../../../client/js/components/form/buttons/FormResetButton'
import DurationUnitField from '../../fields/DurationUnitField'
import useCustomForm from '../../../../../client/js/hooks/useCustomForm'
import { useSettingsContext } from '../../SettingsProvider'

export type LeaseTemplateFormProps = {
  initialValues: Partial<LeaseTemplate>
}

function LeaseTemplateForm (props: LeaseTemplateFormProps) {
  const { initialValues } = props
  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:lease-template')

  const { settings } = useSettingsContext()

  const form = useCustomForm({
    disabled: !editable,
    initialValues,
    initializeField: createFieldInitializer(LeaseTemplateSchema),
    validate: createFormValidator(LeaseTemplateSchema),
    validateField: createFieldValidator(LeaseTemplateSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, LeaseTemplateSchema)
      return id > 0
        ? patchLeaseTemplate(id, finalValues)
        : postLeaseTemplate(finalValues)
    }
  })
  const { values, setValues } = form

  // Handle file uploading.
  const uploader = useMutation({
    mutationKey: ['uploadLeaseTemplate', id],
    mutationFn: async (file: File) => putLeaseTemplateFile(id, file)
  })
  const { mutate: upload } = uploader

  const handleUpload = useCallback((file: File) => {
    setValues({
      size: file.size,
      type: file.type,
      createdAt: file.lastModified
    }, { partial: true })
    upload(file)
  }, [setValues, upload])

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? getLeaseTemplateListLink() : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <SimpleGrid cols={{ lg: 1 }}>
          <Box>
            <FormSection
              defaultOpened
              title={intl.formatMessage({
                id: 'general',
                defaultMessage: 'Général'
              })}
            >
              <NameField />
              <SimpleGrid cols={{ lg: 2 }}>
                <FurnishedField />
                <ManagedField />
              </SimpleGrid>
              <SimpleGrid cols={{ lg: 2 }}>
                <DurationField
                  label={intl.formatMessage({
                    id: 'lease_duration',
                    defaultMessage: 'Durée du bail'
                  })}
                />
                <Group wrap="nowrap" align="end">
                  <DurationField
                    label={intl.formatMessage({
                      id: 'notice_duration',
                      defaultMessage: 'Durée du préavis'
                    })}
                    name="noticeDuration"
                  />
                  <DurationUnitField name="noticeDurationUnit" />
                </Group>
              </SimpleGrid>
            </FormSection>
          </Box>

          <Box>
            {values.id > 0
              ? <FileDropZone
                accept={settings.LEASE_TEMPLATE_FILE_TYPES}
                maxSize={settings.LEASE_TEMPLATE_MAX_UPLOAD_SIZE}
                contentLength={values.size}
                disabled={form.disabled}
                error={uploader.error}
                lastModified={values.createdAt}
                loading={uploader.isPending}
                onChange={handleUpload}
                url={values.id ? `/api/lease-templates/${values.id}/file` : null}
                value={values.name}
              />
              : null}
          </Box>
        </SimpleGrid>

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default LeaseTemplateForm
